import {
  domToBlob,
  domToCanvas,
  domToJpeg,
  waitUntilLoad,
  type Options as ModernScreenshotOptions,
} from 'modern-screenshot'

export type DomScreenshotOptions = {
  width?: number
  height?: number
  quality?: number
  /** 与 html-to-image 的 pixelRatio 对应 modern-screenshot 的 scale */
  pixelRatio?: number
  scale?: number
  type?: 'image/jpeg' | 'image/png'
  backgroundColor?: string
  cacheBust?: boolean
  filter?: ModernScreenshotOptions['filter']
  style?: Partial<CSSStyleDeclaration> | null
  timeout?: number
  drawImageInterval?: number
  /** false 等价于 html-to-image 的 skipFonts: true */
  embedFonts?: boolean
}

const DEFAULT_FEATURES: NonNullable<ModernScreenshotOptions['features']> = {
  removeAbnormalAttributes: true,
  removeControlCharacter: true,
  fixSvgXmlDecode: true,
}

function buildModernScreenshotOptions(options: DomScreenshotOptions): ModernScreenshotOptions {
  const scale = options.scale ?? options.pixelRatio ?? 1
  const cacheBust = !!options.cacheBust
  const screenshotOptions: ModernScreenshotOptions = {
    width: options.width,
    height: options.height,
    quality: options.quality ?? 0.95,
    type: options.type ?? 'image/jpeg',
    scale,
    backgroundColor: options.backgroundColor ?? '#ffffff',
    timeout: options.timeout ?? 30000,
    drawImageInterval: options.drawImageInterval ?? 150,
    fetch: {
      bypassingCache: cacheBust,
      requestInit: {
        cache: cacheBust ? 'reload' : 'force-cache',
      },
    },
    features: DEFAULT_FEATURES,
    filter: options.filter ?? null,
    style: options.style ?? null,
  }
  if (options.embedFonts === false) {
    screenshotOptions.font = false
  }
  return screenshotOptions
}

export async function waitForDomScreenshotLoad(node: Node, timeout = 30000): Promise<void> {
  try {
    await waitUntilLoad(node, { timeout })
  } catch (err) {
    console.warn('DOM 截图：等待资源加载超时，继续尝试', err)
  }
}

export async function captureDomToJpeg(node: HTMLElement, options: DomScreenshotOptions = {}): Promise<string> {
  await waitForDomScreenshotLoad(node, options.timeout)
  return domToJpeg(node, buildModernScreenshotOptions({ ...options, type: 'image/jpeg' }))
}

export async function captureDomToBlob(node: HTMLElement, options: DomScreenshotOptions = {}): Promise<Blob> {
  await waitForDomScreenshotLoad(node, options.timeout)
  return domToBlob(node, buildModernScreenshotOptions(options))
}

export async function captureDomToCanvas(node: HTMLElement, options: DomScreenshotOptions = {}): Promise<HTMLCanvasElement> {
  await waitForDomScreenshotLoad(node, options.timeout)
  return domToCanvas(node, buildModernScreenshotOptions(options))
}
